import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import type {
  SubscriptionRecord,
  SubscriptionState,
  CheckoutSession,
  PortalSession,
} from '@/types/subscription'

/**
 * Hook to manage subscription state and derived values
 */
export function useSubscription(): SubscriptionState {
  const supabase = createClient()

  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .single()

      if (error) {
        // Return null if no subscription found (e.g., user just signed up)
        if (error.code === 'PGRST116') {
          return null
        }
        throw error
      }
      return data as SubscriptionRecord
    },
  })

  // Derived state
  const now = new Date()
  const trialEndsAt = subscription ? new Date(subscription.trial_ends_at) : null

  const isTrialActive =
    subscription?.status === 'trial' &&
    trialEndsAt !== null &&
    trialEndsAt > now

  const isTrialExpired =
    subscription?.status === 'trial' &&
    trialEndsAt !== null &&
    trialEndsAt <= now

  const isPaid = subscription?.status === 'active'

  // Read-only = expired trial or canceled/expired subscription
  const isReadOnly =
    isTrialExpired ||
    subscription?.status === 'expired' ||
    subscription?.status === 'canceled'

  // Can create if not read-only AND under limits
  const canCreateRecording =
    !isReadOnly &&
    subscription !== null &&
    subscription !== undefined &&
    subscription.recordings_used < subscription.recordings_limit

  const canAddGuest =
    !isReadOnly &&
    subscription !== null &&
    subscription !== undefined &&
    subscription.guests_used < subscription.guests_limit

  const recordingsRemaining = subscription
    ? subscription.recordings_limit - subscription.recordings_used
    : 0

  const guestsRemaining = subscription
    ? subscription.guests_limit - subscription.guests_used
    : 0

  // Series limits
  const canCreateSeries =
    !isReadOnly &&
    subscription !== null &&
    subscription !== undefined &&
    subscription.series_used < subscription.series_limit

  const seriesRemaining = subscription
    ? subscription.series_limit - subscription.series_used
    : 0

  const episodesPerSeriesLimit = subscription?.episodes_per_series_limit ?? 4

  // Trial days remaining
  const trialDaysRemaining =
    isTrialActive && trialEndsAt
      ? Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      : null

  return {
    subscription: subscription ?? null,
    isLoading,
    isTrialActive,
    isTrialExpired,
    isPaid,
    isReadOnly,
    canCreateRecording,
    canAddGuest,
    recordingsRemaining,
    guestsRemaining,
    canCreateSeries,
    seriesRemaining,
    episodesPerSeriesLimit,
    trialDaysRemaining,
  }
}

/**
 * Mutation hook to create a Stripe Checkout session
 */
export function useCheckout() {
  const supabase = createClient()

  return useMutation({
    mutationFn: async (priceId: string) => {
      const { data, error } = await supabase.functions.invoke<CheckoutSession>(
        'create-checkout',
        {
          body: { price_id: priceId },
        }
      )

      if (error) throw error
      if (!data) throw new Error('No checkout session returned')
      return data
    },
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      window.location.href = data.url
    },
  })
}

/**
 * Mutation hook to create a Stripe Customer Portal session
 */
export function useCustomerPortal() {
  const supabase = createClient()

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<PortalSession>(
        'create-portal'
      )

      if (error) throw error
      if (!data) throw new Error('No portal session returned')
      return data
    },
    onSuccess: (data) => {
      // Redirect to Stripe Customer Portal
      window.location.href = data.url
    },
  })
}

/**
 * Mutation hook to increment usage counters
 * Call after successfully creating a recording or adding a guest
 */
export function useIncrementUsage() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (type: 'recording' | 'guest') => {
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) throw new Error('Not authenticated')

      // Fetch current subscription - use type assertion to bypass inference issue
      const { data: current, error: fetchError } = await supabase
        .from('subscriptions')
        .select('recordings_used, guests_used')
        .eq('user_id', user.id)
        .single() as { data: { recordings_used: number; guests_used: number } | null; error: Error | null }

      if (fetchError || !current) throw new Error('No subscription found')

      // Increment the appropriate counter
      if (type === 'recording') {
        const { error: updateError } = await supabase
          .from('subscriptions')
          .update({ recordings_used: current.recordings_used + 1 } as never)
          .eq('user_id', user.id)

        if (updateError) throw updateError
      } else {
        const { error: updateError } = await supabase
          .from('subscriptions')
          .update({ guests_used: current.guests_used + 1 } as never)
          .eq('user_id', user.id)

        if (updateError) throw updateError
      }
    },
    onSuccess: () => {
      // Invalidate subscription query to refetch updated usage
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    },
  })
}
